/**
 * P6 enums and small helpers — string unions for status / pred_type and the
 * formatting utilities that operate on them. Centralized so views stay
 * consistent and don't sprinkle raw strings through their JSX.
 */

export type ActivityStatus = "TK_NotStart" | "TK_Active" | "TK_Complete";

// The value companion shares the type's name on purpose (enum-style); a type
// alias + a const isn't a declaration merge, so no-redeclare flags it here.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ActivityStatus = {
  NotStart: "TK_NotStart",
  Active: "TK_Active",
  Complete: "TK_Complete",
} as const;

/** Start (TT_Mile) and finish (TT_FinMile) milestones. Compared upper-cased
 *  to tolerate the TT_TASK-style casing inconsistency in some XER exports. */
const MILESTONE_TASK_TYPES = new Set(["TT_MILE", "TT_FINMILE"]);
/** Task types P6's Expected Finish pin does not apply to — mirrors the
 *  backend's forward_pass._PIN_EXEMPT_TASK_TYPES. */
const PIN_EXEMPT_TASK_TYPES = new Set(["TT_MILE", "TT_FINMILE", "TT_LOE"]);

export function isMilestoneTaskType(taskType: string | null | undefined): boolean {
  return taskType != null && MILESTONE_TASK_TYPES.has(taskType.toUpperCase());
}

export function isPinExemptTaskType(taskType: string | null | undefined): boolean {
  return taskType != null && PIN_EXEMPT_TASK_TYPES.has(taskType.toUpperCase());
}

export const PRED_TYPES = ["PR_FS", "PR_SS", "PR_FF", "PR_SF"] as const;
export type PredType = typeof PRED_TYPES[number];

const PRED_TYPE_LABELS: Record<PredType, string> = {
  PR_FS: "FS",
  PR_SS: "SS",
  PR_FF: "FF",
  PR_SF: "SF",
};

export function predTypeLabel(t: string): string {
  return PRED_TYPE_LABELS[t as PredType] ?? t;
}

/** "+1d", "-2d", "+0.5d", or "" — duration suffix for predecessor lag.
 * Hours that aren't multiples of 8 show one decimal (4h → 0.5d) rather than
 * silently rounding, since 4h and 12h lags do exist in P6 schedules. */
export function formatLagSuffix(lagHours: number): string {
  if (lagHours === 0) return "";
  const days = lagHours / 8;
  const rendered = Number.isInteger(days) ? days.toString() : days.toFixed(1);
  return days > 0 ? `+${rendered}d` : `${rendered}d`;
}

/** Compact relationship label used in tables: "A1030 (FS+1d)". */
export function formatRelLabel(taskCode: string, predType: string, lagHours: number): string {
  return `${taskCode} (${predTypeLabel(predType)}${formatLagSuffix(lagHours)})`;
}

/** True when an activity is on the critical path AND not yet completed. */
export function isCritical(
  totalFloat: number | null | undefined,
  status: string | null | undefined,
): boolean {
  return totalFloat !== null && totalFloat !== undefined && totalFloat <= 0 && status !== "TK_Complete";
}

/** P6 date constraints (CS_*). The forward pass floors on MSOA (start) and
 *  MEOA (finish) and ceilings on MEOB; the rest are carried for display. */
export const CONSTRAINT_TYPE_LABELS: Record<string, string> = {
  CS_MSOA: "Start on or after",
  CS_MSOB: "Start on or before",
  CS_MEOA: "Finish on or after",
  CS_MEOB: "Finish on or before",
  CS_MSO: "Mandatory start",
  CS_MEO: "Mandatory finish",
  CS_ALAP: "As late as possible",
};

export function constraintLabel(cstrType: string): string {
  return CONSTRAINT_TYPE_LABELS[cstrType] ?? cstrType;
}

/** Compact label for tight spots like the gantt, e.g. "Start ≥". */
const CONSTRAINT_TYPE_SHORT: Record<string, string> = {
  CS_MSOA: "Start ≥",
  CS_MSOB: "Start ≤",
  CS_MEOA: "Finish ≥",
  CS_MEOB: "Finish ≤",
  CS_MSO: "Start =",
  CS_MEO: "Finish =",
  CS_ALAP: "ALAP",
};

const START_CONSTRAINTS = new Set(["CS_MSOA", "CS_MSOB", "CS_MSO"]);
const FINISH_CONSTRAINTS = new Set(["CS_MEOA", "CS_MEOB", "CS_MEO"]);

/** Which date a constraint acts on — so a surface can place it under the right
 *  field (Start vs Finish). null for non-date constraints (e.g. ALAP). */
export function constraintAxis(
  cstrType: string | null | undefined,
): "start" | "finish" | null {
  if (!cstrType) return null;
  if (START_CONSTRAINTS.has(cstrType)) return "start";
  if (FINISH_CONSTRAINTS.has(cstrType)) return "finish";
  return null;
}

export interface ConstraintInfo {
  /** Human label, e.g. "Start on or after". */
  label: string;
  /** Compact label for the chart, e.g. "Start ≥". */
  short: string;
  /** Constraint date, normalized to "YYYY-MM-DD". */
  date: string;
  /** The relevant computed date (start for start-constraints, finish for
   *  finish-constraints) lands on the constraint date — i.e. the constraint,
   *  not a predecessor, is what's holding this activity's date. */
  driving: boolean;
}

/** Resolve an activity's date constraint into a display badge, or null when it
 *  has none. `startDate` / `finishDate` are the activity's computed dates; pass
 *  whichever the surface has (the drive dates in the dep editor, the new dates
 *  in the cascade view, the display dates in the activity modal). */
export function constraintInfo(
  cstrType: string | null | undefined,
  cstrDate: string | null | undefined,
  startDate: string | null | undefined,
  finishDate: string | null | undefined,
): ConstraintInfo | null {
  if (!cstrType || !cstrDate) return null;
  const cd = cstrDate.slice(0, 10);
  const axis = constraintAxis(cstrType);
  const cmp = axis === "start" ? startDate : axis === "finish" ? finishDate : null;
  const driving = cmp != null && cmp.slice(0, 10) === cd;
  return {
    label: constraintLabel(cstrType),
    short: CONSTRAINT_TYPE_SHORT[cstrType] ?? cstrType,
    date: cd,
    driving,
  };
}
